// Generated with util/create-component.js
import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Drawer, notification } from 'antd';
import { TablePaginationConfig } from 'antd';

import SearchInput from 'components/search_input/search_input';
import PromoCodes from 'components/promo_codes/promo_codes';
import PromoCodesFrom from 'components/promo_codes_form/promo_codes_form';

import DRAWER_SIZE from 'constants/gift_card_drawer_size';

import { IPromoCodesPageProps } from './promo_codes_page.types';
import { IPromoCodeItem, IPromoCodesFilters } from 'components/promo_codes/promo_codes.types';

import {
  PropertySettingsDataContext,
  PropertySettingsActionsContext,
  PromoCodesDataContext,
  PromoCodesActionsContext,
} from 'containers/data_context';

import styles from './promo_codes_page.module.scss';

const DEFAULT_PAGINATION = { page: 1, limit: 10 };
const DEFAULT_ORDER = { orderBy: 'created_at', orderDirection: 'desc' };
const DEFAULT_FILTERS: IPromoCodesFilters = { pagination: DEFAULT_PAGINATION, ...DEFAULT_ORDER };

const promoCodeInitialValue: IPromoCodeItem = {
  name: '',
  code: '',
  codeType: 'percentage',
  amount: 10,
  status: 'inactive',
  validFrom: '',
  validTo: '',
  usageLimit: null,
  appliesTo: [],
};

const PromoCodesPage: React.FC<IPromoCodesPageProps> = () => {
  const { t } = useTranslation();
  const {
    selectedProperty,
    propertyGiftCardTypesStores: {
      data: propertyGiftCardTypesStoresData,
      isLoading: propertyGiftCardTypesStoresLoading,
    },
  } = useContext(PropertySettingsDataContext);
  const {
    promoCodes: { data: promoCodesData, isLoading: isLoadingPromoCodes },
    promoCode: { isLoading: isLoadingPromoCode },
  } = useContext(PromoCodesDataContext);
  const { loadPromoCodes, loadPromoCode, createPromoCode, updatePromoCode } = useContext(PromoCodesActionsContext);
  const { loadPropertyGiftCardTypesStores } = useContext(PropertySettingsActionsContext);
  const [allFilters, setFilters] = useState(DEFAULT_FILTERS);
  const [showPromoCodeDrawer, setShowPromoCodeDrawer] = useState<boolean>(false);
  const [promoCode, setPromoCode] = useState<IPromoCodeItem>(promoCodeInitialValue);
  useEffect(() => {
    if (selectedProperty) {
      loadPromoCodes({ ...allFilters, propertyId: selectedProperty });
    }
  }, [selectedProperty, allFilters]);
  useEffect(() => {
    if (selectedProperty) {
      loadPropertyGiftCardTypesStores(selectedProperty);
    }
  }, [selectedProperty]);
  const pagination: TablePaginationConfig = {
    current: allFilters.pagination?.page,
    total: promoCodesData?.meta?.total,
    pageSize: allFilters.pagination?.limit,
    hideOnSinglePage: false,
    showSizeChanger: false,
  };
  const handleSearch = async (term: string) => {
    setFilters({ ...allFilters, term: term, pagination: DEFAULT_PAGINATION });
  };
  const openPromoCodeFormDrawer = () => {
    setShowPromoCodeDrawer(true);
  };
  const closePromoCodeFormDrawer = () => {
    setShowPromoCodeDrawer(false);
  };
  const openCreateNewPromoCodeDrawer = () => {
    setPromoCode(promoCodeInitialValue);
    openPromoCodeFormDrawer();
  };
  const openNotificationWithIcon = (type: 'success' | 'error') => {
    if (type === 'success') {
      return notification['success']({
        message: t('general.success_message'),
      });
    } else {
      return notification['error']({
        message: t('general.error_message'),
        description: t('general.error_description'),
      });
    }
  };
  const handleEdit = async (promoCode: IPromoCodeItem) => {
    const { id } = promoCode;
    openPromoCodeFormDrawer();
    try {
      const code = await loadPromoCode({ propertyId: selectedProperty, promoCodeId: id });
      setPromoCode(code);
    } catch (e) {
      console.error(e);
      openPromoCodeFormDrawer();
    }
  };

  const handleSubmit = async (values: IPromoCodeItem): Promise<void> => {
    try {
      const savePromoCode = { promoCodeParams: { ...values } };
      const method = values.id ? updatePromoCode : createPromoCode;
      const payload = values.id
        ? { propertyId: selectedProperty, promoCode: { ...savePromoCode, id: values.id } }
        : { ...savePromoCode, propertyId: selectedProperty };
      const data = await method(payload);
      if (data) {
        loadPromoCodes({ ...allFilters, propertyId: selectedProperty });
        closePromoCodeFormDrawer();
        openNotificationWithIcon('success');
        return data;
      }
    } catch (error) {
      console.error('Error saving promo code:', error);
      if (error instanceof Error) {
        if (
          error?.message?.includes('code with the same code already exists') ||
          error?.message?.includes('is under minimum price of')
        ) {
          return notification['error']({
            message: `${error.message}`,
          });
        } else {
          openNotificationWithIcon('error');
        }
      }
    }
  };

  const handlePageChange = async (currentPagination: TablePaginationConfig) => {
    const { current, pageSize } = currentPagination;
    const pagination = {
      page: current || DEFAULT_PAGINATION?.page,
      limit: pageSize || DEFAULT_PAGINATION?.limit,
    };
    setFilters({ ...allFilters, pagination });
  };

  const promoCodeLoading = isLoadingPromoCodes || propertyGiftCardTypesStoresLoading || isLoadingPromoCode;

  return (
    <div data-testid="PromoCodesPage" className={styles.root}>
      <div className={styles.listWrap}>
        <div className={styles.createButton}>
          <Button onClick={openCreateNewPromoCodeDrawer} type="primary" htmlType="submit" size="large">
            {t('promo_codes.create_new')}
          </Button>
        </div>
        <SearchInput placeholder={t('promo_codes.search')} onSearch={handleSearch} variant="borderless" />
        <PromoCodes
          promoCodes={promoCodesData?.data}
          loading={isLoadingPromoCodes}
          handleEdit={handleEdit}
          pagination={pagination}
          handlePageChange={handlePageChange}
        />
      </div>
      <Drawer
        title={t('promo_codes.create_new')}
        open={showPromoCodeDrawer}
        onClose={closePromoCodeFormDrawer}
        width={window.innerWidth > 900 ? DRAWER_SIZE.LARGE : window.innerWidth}
        destroyOnClose
        loading={promoCodeLoading}
      >
        <PromoCodesFrom
          promoCode={promoCode}
          giftCardStoreTypes={propertyGiftCardTypesStoresData?.storeGiftCardTypes}
          onSave={handleSubmit}
        />
      </Drawer>
    </div>
  );
};

export default PromoCodesPage;
